import React, { useState } from 'react'
import {
  SelectTestFieldName,
  useLocationTestProducts,
  ITestSelection,
  CategoryTable,
  CategoryType,
  ProductTypes,
  SubLocation,
} from '@modmd/data'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR, fontSizes } from 'theme'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { Loading } from 'components/Loading'
import { LoadingSkeleton } from 'components/Datalist'

const SKELETON_ROWS = 3
const SKELETON_COLUMNS = 3

const MAX_VALUE_PER_TEST = 9999

export const TestSelection: React.VFC<ITestSelection> = ({
  isWalkIn,
  categoryType,
  selectTestForm,
  testLocationId,
  testSessionForm,
  isInsurance = false,
}) => {
  const [quantityTotal, setQuantityTotal] = useState(0)
  const { products, isProductsDataLoading } = useLocationTestProducts({
    testLocationId,
    ...(!isWalkIn && { sublocation: SubLocation.Concierge }),
  })

  function walkInProduct(product: { name: string }) {
    return product.name.includes('Walk In')
  }

  function notWalkInProduct(product: { name: string }) {
    return !product.name.includes('Walk In')
  }

  const getCategoryType = (testType: ProductTypes): CategoryType =>
    (CategoryTable[testType] as unknown) as CategoryType

  const getProducts = (isWalkIn: boolean) =>
    products.filter((product) =>
      isWalkIn
        ? walkInProduct(product) && categoryType === getCategoryType(product.type!)
        : notWalkInProduct(product) && categoryType === getCategoryType(product.type!)
    )

  const productsList = getProducts(!!isWalkIn)

  React.useEffect(() => {
    selectTestForm.setFieldError(SelectTestFieldName.TEST_MEMBER_MISMATCH, undefined)
    selectTestForm.setFieldValue(SelectTestFieldName.IS_CONFIRMED_TESTS_EXCEEDING, false)
    if (isInsurance && selectTestForm.values.testCount > 1) {
      setQuantityTotal(0)
      selectTestForm.setFieldValue(SelectTestFieldName.TESTS, [])
      selectTestForm.setFieldValue(SelectTestFieldName.TEST_COUNT, 0)
    } else {
      setQuantityTotal(selectTestForm.values.testCount)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isProductsDataLoading) {
    return (
      <Loading
        isLoading
        skeletonComp={<LoadingSkeleton rows={SKELETON_ROWS} columns={SKELETON_COLUMNS} />}
      />
    )
  }

  if (!testLocationId) {
    return (
      <Text textAlign="center" color={COLOR.danger}>
        There are no tests available for your address.
      </Text>
    )
  }

  return (
    <Box display="grid" gridGap="0.75rem">
      <Text color={COLOR.grayDark}>
        Please manually add the tests you are ordering by scrolling through the available tests
        below.
      </Text>
      {productsList
        .filter((e) => !e.name.startsWith('Antigen Test'))
        .map((product) => {
          const testIndex = selectTestForm.values.tests.findIndex(
            ({ productId }) => productId === product.id
          )
          const currentQuantity = selectTestForm.values.tests[testIndex]?.quantity ?? 0
          const productPrice = product.Prices.find(
            ({ minQuantity }) => currentQuantity >= minQuantity
          )!
          const test = selectTestForm.values.tests[testIndex] || {
            productId: product.id,
            type: product.type,
            priceId: productPrice?.id,
            quantity: 0,
          }
          const handleQuantity = (quantity: number) => {
            if (quantityTotal === 1 && isInsurance && quantityTotal > quantityTotal + quantity)
              return
            const newProductPrice = product.Prices.find(
              ({ minQuantity }) => quantity >= minQuantity
            )!
            const editedTest = { ...test, priceId: newProductPrice?.id, quantity }
            setQuantityTotal(quantity)

            if (testIndex >= 0) {
              if (quantity === 0) {
                selectTestForm.setFieldValue(SelectTestFieldName.TESTS, [
                  ...selectTestForm.values.tests.slice(0, testIndex),
                  ...selectTestForm.values.tests.slice(testIndex + 1),
                ])
                selectTestForm.setFieldValue(SelectTestFieldName.TEST_COUNT, 0)
              } else
                selectTestForm.setFieldValue(SelectTestFieldName.TESTS, [
                  ...selectTestForm.values.tests.slice(0, testIndex),
                  editedTest,
                  ...selectTestForm.values.tests.slice(testIndex + 1),
                ])
            } else {
              selectTestForm.setFieldValue(SelectTestFieldName.TESTS, [
                ...selectTestForm.values.tests,
                editedTest,
              ])
            }
          }

          const handleSubtractExam = () => {
            handleQuantity(Math.max(test.quantity - 1, 0))
            if (testSessionForm.values.Members.length > 0) testSessionForm.values.Members.pop()
          }

          const isPriceAvailable = productPrice?.price
            ? `- $${productPrice?.price}`
            : '- The quantity is not available'

          return (
            <Box key={product.id} display="flex" flexDirection="column" marginTop="1rem">
              <Box
                display="flex"
                height="3rem"
                alignItems="center"
                borderTop={`1px solid ${COLOR.gray}`}
              >
                <Text fontSize={fontSizes.l}>
                  {product.name} {!isInsurance ? isPriceAvailable : ''}
                </Text>
              </Box>
              {test.quantity > 0 && productPrice?.price && !isInsurance && (
                <Text color={COLOR.brand}>${test.quantity * productPrice?.price}</Text>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Text color={COLOR.grayDark}>Count: {test.quantity}</Text>
                <Box
                  width="6.5rem"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {test.quantity > 0 ? (
                    <>
                      <Button onClick={handleSubtractExam}>
                        <Icon.Minus size="L" />
                      </Button>
                      <Button
                        onClick={() => {
                          handleQuantity(Math.min(test.quantity + 1, MAX_VALUE_PER_TEST))
                        }}
                        disabled={quantityTotal > 0 && isInsurance}
                      >
                        <Icon.Plus size="L" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      width="6.5rem"
                      onClick={() => {
                        handleQuantity(Math.min(test.quantity + 1, MAX_VALUE_PER_TEST))
                      }}
                      disabled={quantityTotal > 0 && isInsurance}
                    >
                      Add
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )
        })}

      <Text my="0.5rem" textAlign="center" color={COLOR.danger}>
        {selectTestForm.errors.testCount}
      </Text>
      <Text color={COLOR.grayDark}>
        If you have insurance we can provide a superbill for you to send to your insurance for
        reimbursement - the amount they reimburse will be dependent on your insurance.
      </Text>
    </Box>
  )
}
